import React, { useMemo, useState } from 'react';
import { useGetSettingByKeyQuery, useUpsertSettingMutation } from '../../../../../reducers/settingsApi';
import { toast, ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";

const PriceVariables = () => {
  const { data: response, isLoading, error } = useGetSettingByKeyQuery('app.pricinghistory');
  const [upsertSetting, { isLoading: isUpdating }] = useUpsertSettingMutation();

  const [editingVar, setEditingVar] = useState(null);
  const [editValue, setEditValue] = useState('');

  const rawData = useMemo(() => {
    if (!response?.success || !response?.data?.value) return null;
    let value = response.data.value;
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch (e) {
        console.error("Failed to parse pricing variables:", e);
        return null;
      }
    }
    return value;
  }, [response]);

  const variables = useMemo(() => {
    if (!rawData) return [];

    const { veriables } = rawData;
    const list = [];

    const formatValue = (key, val) => {
      if (key === 'aggressive' || key === 'conservative') return `${val}% Lower`;
      if (key === 'justBelow') return `$${val} Lower`;
      if (key === 'priceFloorMargin') return `${val}% Minimum Margin`;
      if (key === 'outdated') return `${val} Days`;
      if (key === 'maxDiscount') return `$${val} Maximum Discount`;
      return val;
    };

    const formatName = (key) => {
      if (key === 'priceFloorMargin') return 'Price Floor Margin';
      if (key === 'maxDiscount') return 'Max Discount';
      return key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
    };

    const allVariables = { ...(veriables || {}) };
    if (allVariables.maxDiscount === undefined) {
      allVariables.maxDiscount = 0;
    }

    Object.entries(allVariables).forEach(([key, val]) => {
      list.push({
        id: key,
        name: formatName(key),
        displayValue: formatValue(key, val),
        rawValue: val
      });
    });

    return list;
  }, [rawData]);

  const handleEditClick = (v) => {
    setEditingVar(v);
    setEditValue(v.rawValue);
  };

  const handleSave = async () => {
    if (!editingVar || editValue === '') return;

    try {
      const veriablesPayload = { ...rawData.veriables };

      // Ensure maxDiscount is added if it doesn't exist
      if (veriablesPayload.maxDiscount === undefined) {
        veriablesPayload.maxDiscount = 0;
      }

      veriablesPayload[editingVar.id] = Number(editValue);

      const newValue = {
        ...rawData,
        veriables: veriablesPayload
      };

      const payload = {
        key: 'app.pricinghistory',
        value: newValue,
        description: response.data.description || "Sundial Pricing History",
        category: response.data.category || "app"
      };

      const res = await upsertSetting(payload).unwrap();
      if (res.success) {
        toast.success(`${editingVar.name} updated successfully`, { containerId: "V" });
        setEditingVar(null);
      } else {
        toast.error(res.message || 'Failed to update variable', { containerId: "V" });
      }
    } catch (err) {
      toast.error(err?.data?.message || 'Something went wrong', { containerId: "V" });
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 bg-white text-center">
        <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
        <span className="ml-2">Loading variables...</span>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white">
      <div className="overflow-auto border-top">
        <table className="table">
          <thead>
            <tr>
              <th style={{ borderTop: 'none', fontWeight: 'bold' }}>Variable</th>
              <th style={{ borderTop: 'none', fontWeight: 'bold' }}>Description</th>
              <th style={{ borderTop: 'none' }}></th>
            </tr>
          </thead>
          <tbody>
            {variables.map((v) => (
              <tr key={v.id}>
                <td className="align-middle">{v.name}</td>
                <td className="align-middle">{v.displayValue}</td>
                <td className="text-right align-middle" style={{ textAlign: 'right' }}>
                  <button
                    className="btn btn-sm btn-outline-dark rounded-pill px-4"
                    style={{
                      border: '1px solid #ced4da',
                      backgroundColor: 'white',
                      color: '#212529',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                    onClick={() => handleEditClick(v)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Basic Edit Modal/Form */}
      {editingVar && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title font-weight-bold">Update {editingVar.name}</h5>
                <button type="button" className="close" onClick={() => setEditingVar(null)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body py-4">
                <div className="form-group">
                  <label className="text-muted small font-weight-bold text-uppercase mb-2">
                    Value
                  </label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      autoFocus
                    />
                    {(editingVar.id === 'justBelow' || editingVar.id === 'maxDiscount') && <div className="input-group-append"><span className="input-group-text">$</span></div>}
                    {(editingVar.id === 'aggressive' || editingVar.id === 'conservative' || editingVar.id === 'priceFloorMargin') && <div className="input-group-append"><span className="input-group-text">%</span></div>}
                    {editingVar.id === 'outdated' && <div className="input-group-append"><span className="input-group-text">Days</span></div>}
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-light rounded-pill px-4 mr-2"
                  onClick={() => setEditingVar(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-dark rounded-pill px-4"
                  disabled={isUpdating}
                  onClick={handleSave}
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Toast notifications */}
      <ToastContainer
        enableMultiContainer
        containerId={"V"}
        position="top-right"
      />
    </div>
  );
};

export default PriceVariables;
